import ArrowBackIcon from '@material-design-icons/svg/filled/arrow_back.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import ReplayIcon from '@material-design-icons/svg/filled/replay.svg?react';
import SearchIcon from '@material-design-icons/svg/filled/search.svg?react';
import SendIcon from '@material-design-icons/svg/filled/send.svg?react';
import { sumBy } from 'es-toolkit/math';
import { useState } from 'react';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { DappReviewSummaryAppCard } from './DappReviewSummaryAppCard';
import { SectionCard, SectionHeader, StatusPill } from './primitives';
import { Button } from './uiButton';
import {
  formatAppStatus,
  formatDateTime,
  formatDuration,
  formatRelative,
  formatRunStatus,
  formatWindow,
  getAppStatusVariant,
  getRunStatusVariant,
} from './runFormatting';

type RunDetailResult = RightResult<
  RouterOutputs['getDappReviewSummaryRunDetail']
>;
type AppSummary = RunDetailResult['summaries'][number];

interface Props {
  runId: string;
  detail: RunDetailResult | null;
  loading: boolean;
  retryingFailed?: boolean;
  onBack: () => void;
  onRefresh: () => void;
  onRetryFailed?: () => void;
  onSendPublisherEmails?: () => void;
  sendingPublisherEmails?: boolean;
}

const STATUS_FILTERS: Array<{
  value: 'all' | AppSummary['status'];
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'Completed', label: 'Completed' },
  { value: 'NoUsefulReviews', label: 'No useful' },
  { value: 'NoReviews', label: 'No reviews' },
  { value: 'SourceUnavailable', label: 'Source missing' },
  { value: 'Error', label: 'Error' },
];

export function DappReviewSummaryRunDetail({
  runId,
  detail,
  loading,
  retryingFailed,
  onBack,
  onRefresh,
  onRetryFailed,
  onSendPublisherEmails,
  sendingPublisherEmails,
}: Props) {
  const [statusFilter, setStatusFilter] = useState<
    'all' | AppSummary['status']
  >('all');
  const [searchTerm, setSearchTerm] = useState('');

  const term = searchTerm.trim().toLowerCase();
  const filteredSummaries = (detail?.summaries ?? []).filter((summary) => {
    if (statusFilter !== 'all' && summary.status !== statusFilter) {
      return false;
    }
    if (!term) {
      return true;
    }
    return (
      summary.androidPackage.toLowerCase().includes(term) ||
      (summary.dappName?.toLowerCase().includes(term) ?? false)
    );
  });

  const statusCounts = new Map<string, number>();
  for (const summary of detail?.summaries ?? []) {
    statusCounts.set(
      summary.status,
      (statusCounts.get(summary.status) ?? 0) + 1,
    );
  }
  const failedSummaryCount = statusCounts.get('Error') ?? 0;
  const publisherEmailCandidateCount = (detail?.summaries ?? []).filter(
    isPublisherEmailCandidate,
  ).length;

  const reviewCountDistribution = REVIEW_VOLUME_BUCKETS.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    appCount: 0,
  }));

  for (const summary of detail?.summaries ?? []) {
    const index = REVIEW_VOLUME_BUCKETS.findIndex((bucket) =>
      bucket.match(summary.inputReviewCount),
    );
    if (index >= 0) {
      reviewCountDistribution[index].appCount += 1;
    }
  }

  if (loading && !detail) {
    return (
      <div className="space-y-6">
        <BackBar onBack={onBack} />
        <SectionCard>
          <div className="flex h-32 items-center justify-center text-sm text-neutral-500">
            Loading run…
          </div>
        </SectionCard>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-6">
        <BackBar onBack={onBack} />
        <SectionCard>
          <div className="px-6 py-12 text-center text-sm text-neutral-500">
            Run not found (id: {runId}).
          </div>
        </SectionCard>
      </div>
    );
  }

  const { run, summaries } = detail;
  const duration = formatDuration(run.startedAt, run.completedAt);
  const runVariant = getRunStatusVariant(run.status);
  const summaryEmailsSent = run.summaryEmailsSent ?? 0;
  const summaryEmailsSkipped = run.summaryEmailsSkipped ?? 0;
  const summaryEmailFailures = run.summaryEmailFailures ?? 0;

  return (
    <div className="space-y-6">
      <BackBar
        onBack={onBack}
        onRefresh={onRefresh}
        refreshing={loading}
        failedSummaryCount={failedSummaryCount}
        retryingFailed={retryingFailed}
        retryFailedDisabled={run.status === 'Running'}
        onRetryFailed={onRetryFailed}
        publisherEmailCandidateCount={publisherEmailCandidateCount}
        sendingPublisherEmails={sendingPublisherEmails}
        sendPublisherEmailsDisabled={run.status === 'Running'}
        onSendPublisherEmails={onSendPublisherEmails}
      />

      <SectionCard>
        <header className="flex flex-col gap-4 border-b border-white/[0.06] px-6 py-5 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">
              Run {run.id.slice(0, 8)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold tracking-tight text-white">
                {formatWindow(run.windowStart, run.windowEnd)}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-neutral-500">
              <StatusPill variant={runVariant}>
                {formatRunStatus(run.status)}
              </StatusPill>
              <span className="rounded-full border border-white/[0.06] bg-white/[0.02] px-2 py-0.5 text-[11px] font-medium text-neutral-300">
                {run.trigger}
              </span>
              <span>·</span>
              <span>
                Started{' '}
                {formatRelative(run.startedAt) ?? formatDateTime(run.startedAt)}
              </span>
              {duration && (
                <>
                  <span>·</span>
                  <span>{duration}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">
              Run id
            </div>
            <div className="mt-1 font-mono text-[11px] text-neutral-400 break-all">
              {run.id}
            </div>
          </div>
        </header>

        <div className="grid grid-cols-2 divide-x divide-y divide-white/[0.05] md:grid-cols-4 lg:grid-cols-8">
          <Stat label="Started" value={formatDateTime(run.startedAt)} />
          <Stat label="Completed" value={formatDateTime(run.completedAt)} />
          <Stat label="Duration" value={duration ?? '—'} />
          <Stat label="Apps scanned" value={String(run.appsScanned)} />
          <Stat
            label="Store listings"
            value={String(run.appsWithStoreListing)}
          />
          <Stat
            label="Reviews used"
            value={`${run.reviewsIncluded}/${run.reviewsFetched}`}
          />
          <Stat
            label="Summaries"
            value={String(run.summariesWritten)}
            tone="success"
          />
          <Stat
            label="Emails"
            value={`${summaryEmailsSent}/${summaryEmailsSkipped + summaryEmailFailures}`}
            tone={summaryEmailFailures > 0 ? 'error' : undefined}
          />
        </div>

        {run.errorMessage && (
          <div className="border-t border-white/[0.06] px-6 py-4">
            <div className="rounded-lg border border-rose-400/20 bg-rose-400/10 px-3.5 py-2.5 text-sm text-rose-200">
              {run.errorMessage}
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard>
        <SectionHeader
          eyebrow="Output"
          title="Per-app summaries"
          description={`${summaries.length} app${summaries.length === 1 ? '' : 's'} processed. Expand a card to read its summary and the reviews it was built from.`}
          actions={
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 fill-neutral-500" />
              <input
                type="search"
                aria-label="Filter by app or package"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Filter by app or package"
                className="h-9 w-full rounded-lg border border-white/[0.06] bg-white/[0.02] pl-9 pr-3 text-[13px] text-white outline-none transition-colors placeholder:text-neutral-500 focus:border-teal-400/40 focus:bg-white/[0.04] md:w-64"
              />
            </div>
          }
        />

        <div className="flex flex-wrap items-center gap-1.5 border-b border-white/[0.06] px-6 py-3">
          {STATUS_FILTERS.map((filter) => {
            const isActive = statusFilter === filter.value;
            const count =
              filter.value === 'all'
                ? summaries.length
                : (statusCounts.get(filter.value) ?? 0);
            return (
              <button
                key={filter.value}
                type="button"
                onClick={() => setStatusFilter(filter.value)}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12px] font-medium transition-colors ${
                  isActive
                    ? 'bg-teal-400/10 text-teal-200 ring-1 ring-inset ring-teal-400/30'
                    : 'text-neutral-400 hover:bg-white/[0.04] hover:text-neutral-100'
                }`}
              >
                {filter.label}
                <span
                  className={`tabular-nums text-[11px] ${
                    isActive ? 'text-teal-300/80' : 'text-neutral-500'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <ReviewVolumeChart distribution={reviewCountDistribution} />

        <div className="divide-y divide-white/[0.04]">
          {filteredSummaries.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-neutral-500">
              No summaries match the current filters.
            </div>
          ) : (
            filteredSummaries.map((summary) => (
              <DappReviewSummaryAppCard
                key={summary.id}
                summary={summary}
                variant={getAppStatusVariant(summary.status)}
                statusLabel={formatAppStatus(summary.status)}
              />
            ))
          )}
        </div>
      </SectionCard>
    </div>
  );
}

const REVIEW_VOLUME_BUCKETS: ReadonlyArray<{
  key: string;
  label: string;
  match: (count: number) => boolean;
}> = [
  { key: '0', label: '0 reviews', match: (n) => n === 0 },
  { key: '1', label: '1 review', match: (n) => n === 1 },
  { key: '2-5', label: '2-5 reviews', match: (n) => n >= 2 && n <= 5 },
  { key: '6-10', label: '6-10 reviews', match: (n) => n >= 6 && n <= 10 },
  { key: '11-20', label: '11-20 reviews', match: (n) => n >= 11 && n <= 20 },
  { key: '21+', label: '21+ reviews', match: (n) => n >= 21 },
];

const SENDABLE_PUBLISHER_EMAIL_STATUSES = new Set([
  'Pending',
  'NotRequested',
  'Disabled',
  'Failed',
]);

function isPublisherEmailCandidate(summary: AppSummary) {
  return (
    summary.status === 'Completed' &&
    SENDABLE_PUBLISHER_EMAIL_STATUSES.has(summary.emailStatus) &&
    summary.inputReviewCount > 0 &&
    summary.includedReviewCount > 0 &&
    summary.sourceReviewIds.length > 0 &&
    Boolean(summary.summary?.trim())
  );
}

function ReviewVolumeChart({
  distribution,
}: {
  distribution: Array<{
    key: string;
    label: string;
    appCount: number;
  }>;
}) {
  const totalApps = sumBy(distribution, (bucket) => bucket.appCount);
  if (totalApps === 0) {
    return null;
  }

  const maxAppCount = Math.max(
    ...distribution.map((bucket) => bucket.appCount),
    1,
  );

  return (
    <div className="border-b border-white/[0.06] px-6 py-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-neutral-500">
          Review volume
        </div>
        <div className="text-[11px] text-neutral-500">
          Apps grouped by fetched reviews
        </div>
      </div>
      <div
        className="space-y-2"
        role="img"
        aria-label="Apps grouped by fetched review count"
      >
        {distribution.map((bucket) => {
          const width =
            bucket.appCount === 0
              ? 0
              : Math.max((bucket.appCount / maxAppCount) * 100, 3);

          return (
            <div
              key={bucket.key}
              className="grid grid-cols-[minmax(96px,auto)_minmax(0,1fr)_minmax(58px,auto)] items-center gap-3"
            >
              <div className="text-[12px] font-medium text-neutral-300">
                {bucket.label}
              </div>
              <div className="h-6 overflow-hidden rounded-md border border-white/[0.04] bg-white/[0.025]">
                {bucket.appCount > 0 && (
                  <div
                    className="h-full rounded-md bg-gradient-to-r from-teal-500/65 to-teal-300/80"
                    style={{ width: `${width}%` }}
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="text-right text-[12px] font-semibold tabular-nums text-white">
                {formatAppCount(bucket.appCount)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatAppCount(value: number) {
  return `${value.toLocaleString('en-US')} ${value === 1 ? 'app' : 'apps'}`;
}

function BackBar({
  onBack,
  onRefresh,
  refreshing,
  failedSummaryCount,
  retryingFailed,
  retryFailedDisabled,
  onRetryFailed,
  publisherEmailCandidateCount,
  sendingPublisherEmails,
  sendPublisherEmailsDisabled,
  onSendPublisherEmails,
}: {
  onBack: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  failedSummaryCount?: number;
  retryingFailed?: boolean;
  retryFailedDisabled?: boolean;
  onRetryFailed?: () => void;
  publisherEmailCandidateCount?: number;
  sendingPublisherEmails?: boolean;
  sendPublisherEmailsDisabled?: boolean;
  onSendPublisherEmails?: () => void;
}) {
  const canRetryFailed = Boolean(onRetryFailed && failedSummaryCount);
  const canSendPublisherEmails = Boolean(onSendPublisherEmails);
  const sendDisabled =
    sendingPublisherEmails ||
    sendPublisherEmailsDisabled ||
    !publisherEmailCandidateCount;

  return (
    <div className="flex items-center justify-between">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowBackIcon className="size-3.5 fill-current" />
        Back to runs
      </Button>

      <div className="flex items-center gap-2">
        {canSendPublisherEmails && (
          <Button
            variant="primary"
            size="md"
            onClick={onSendPublisherEmails}
            submitting={sendingPublisherEmails}
            disabled={sendDisabled}
            title={
              sendPublisherEmailsDisabled
                ? 'This run is still running'
                : publisherEmailCandidateCount
                  ? `Send ${publisherEmailCandidateCount} unsent publisher summary ${
                      publisherEmailCandidateCount === 1 ? 'email' : 'emails'
                    }`
                  : 'No unsent completed publisher summary emails are eligible'
            }
          >
            {!sendingPublisherEmails && (
              <SendIcon className="size-3.5 fill-current" />
            )}
            Send all
          </Button>
        )}

        {canRetryFailed && (
          <Button
            variant="secondary"
            size="md"
            onClick={onRetryFailed}
            submitting={retryingFailed}
            disabled={retryingFailed || retryFailedDisabled}
            title={
              retryFailedDisabled
                ? 'This run is already running'
                : `Retry ${failedSummaryCount} failed ${
                    failedSummaryCount === 1 ? 'app' : 'apps'
                  }`
            }
          >
            {!retryingFailed && (
              <ReplayIcon className="size-3.5 fill-current" />
            )}
            Retry {failedSummaryCount} failed
          </Button>
        )}

        {onRefresh && (
          <Button
            variant="secondary"
            size="md"
            onClick={onRefresh}
            submitting={refreshing}
            disabled={refreshing}
          >
            {!refreshing && <RefreshIcon className="size-3.5 fill-current" />}
            Refresh
          </Button>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'error';
}) {
  return (
    <div className="px-5 py-4">
      <div className="text-[11px] uppercase tracking-[0.12em] text-neutral-500">
        {label}
      </div>
      <div
        className={`mt-1 text-[15px] font-semibold tabular-nums ${
          tone === 'error'
            ? 'text-rose-300'
            : tone === 'success'
              ? 'text-emerald-300'
              : 'text-white'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
